import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import sprite from '../../assets/icons/sprite.svg';
import { ChannelContext, ConfigContext } from '../../context';
import { ChannelAppTabAction, ChannelRole, OrganisationRole } from '../../services';
import { getAppTabHrefByActionType, useAppTabsLabel } from '../../shared';

import classNames from 'classnames';
import classes from './MenuAdmin.module.scss';

interface MenuAdminItem {
  label?: string;
  localiseKey?: string;
  iconId?: string;
  highlighted?: boolean;
  href?: string;
  to?: string;
  showSubItems?: boolean;
  setShowSubItems?: (show: boolean) => void;
}

export const MenuAdmin: FunctionComponent = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  const { storyId } = useParams();

  const { getAppTabsLabel } = useAppTabsLabel();

  const {
    role: { id: roleId },
    appTabs,
  } = useContext(ChannelContext).channel;

  const renderChats = useMemo(
    () => appTabs.map(({ action }) => action.type).includes(ChannelAppTabAction.CHATS),
    [appTabs]
  );

  const { adminEnabled: eventsEnabled = false, tabLocaliseKey: eventsTabLocaliseKey = '' } = {
    ...useContext(ConfigContext).config.elements.events,
  };

  const renderSettings = useMemo(
    () =>
      [
        ChannelRole.ADMIN,
        OrganisationRole.ADMIN,
        OrganisationRole.OWNER,
        OrganisationRole.TEAM_MEMBER,
      ].includes(roleId),
    [roleId]
  );

  const storySubItems = appTabs.filter(({ action: { type } }) =>
    [
      ChannelAppTabAction.STORIES,
      ChannelAppTabAction.STORIES_2,
      ChannelAppTabAction.STORIES_3,
      ChannelAppTabAction.STORIES_4,
      ChannelAppTabAction.STORIES_5,
      ChannelAppTabAction.PINNED_STORY,
      ChannelAppTabAction.PINNED_STORY_2,
      ChannelAppTabAction.PINNED_STORY_3,
      ChannelAppTabAction.PINNED_STORY_4,
      ChannelAppTabAction.PINNED_STORY_5,
    ].includes(type)
  );

  const isStorySubItemSelected = useMemo(
    () =>
      [
        '/admin/storyTab1',
        '/admin/storyTab2',
        '/admin/storyTab3',
        '/admin/storyTab4',
        '/admin/storyTab5',
        '/admin/pinnedStoryTab',
        '/admin/pinnedStoryTab2',
        `/admin/storyTab1/${storyId}/items`,
        `/admin/storyTab2/${storyId}/items`,
        `/admin/storyTab3/${storyId}/items`,
        `/admin/storyTab4/${storyId}/items`,
        `/admin/storyTab5/${storyId}/items`,
        `/admin/pinnedStoryTab/${storyId}/items`,
        `/admin/pinnedStoryTab2/${storyId}/items`,
      ].includes(pathname),
    [pathname, storyId]
  );

  const [showStorySubItems, setShowStorySubItems] = useState<boolean>(true);

  const renderStorySubItems = useMemo(
    () => Boolean(showStorySubItems && storySubItems.length),
    [showStorySubItems, storySubItems.length]
  );

  const renderMenuItem = useCallback(
    ({
      label,
      localiseKey,
      iconId,
      to,
      href,
      highlighted,
      showSubItems,
      setShowSubItems,
    }: MenuAdminItem) => {
      const text = label ?? t(`adminLayoutMenu.${localiseKey}`);

      const toggleShowSubItems = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!setShowSubItems) {
          return;
        }
        event.preventDefault();
        setShowSubItems(!showSubItems);
      };

      return (
        <li key={text}>
          {to && (
            <NavLink
              to={to}
              className={({ isActive }) =>
                classNames(classes['menu__item'], {
                  [classes['menu__item--active']]: isActive,
                  [classes['menu__item--highlighted']]: highlighted,
                })
              }
              {...(to === '/admin' && { end: true })}
            >
              {iconId && (
                <svg className={classes['menu__item-icon']}>
                  <use href={`${sprite}#${iconId}`} />
                </svg>
              )}

              <span className={classes['menu__item-label']}>{text}</span>

              {Boolean(setShowSubItems) && (
                <div className={classes['menu__item-toggle']} onClick={toggleShowSubItems}>
                  <svg className={classes['menu__item-toggle-arrow']}>
                    <use href={`${sprite}#${showSubItems ? 'up' : 'down'}-arrow`} />
                  </svg>
                </div>
              )}
            </NavLink>
          )}
          {href && (
            <a className={classes['menu__item']} href={href} target={'_blank'} rel={'noreferrer'}>
              {iconId && (
                <svg className={classes['menu__item-icon']}>
                  <use href={`${sprite}#${iconId}`} />
                </svg>
              )}
              <span className={classes['menu__item-label']}>{text}</span>
            </a>
          )}
        </li>
      );
    },
    [t]
  );

  return (
    <ul className={classes['menu']}>
      {renderMenuItem({ localiseKey: 'content', iconId: 'home', to: '/admin' })}

      {renderMenuItem({
        localiseKey: 'mixes',
        iconId: 'mixes',
        to: '/admin/storyTab',
        highlighted: isStorySubItemSelected,
        showSubItems: showStorySubItems,
        setShowSubItems: setShowStorySubItems,
      })}

      {renderStorySubItems && (
        <li>
          <ul className={classes['menu--sub']}>
            {storySubItems.map(({ action: { type, storyId } }) => {
              return renderMenuItem({
                to: getAppTabHrefByActionType({ actionType: type, storyId }),
                label: getAppTabsLabel(type),
              });
            })}
          </ul>
        </li>
      )}

      {eventsEnabled &&
        renderMenuItem({
          label: t(eventsTabLocaliseKey),
          iconId: 'event',
          to: '/admin/events',
        })}

      {renderMenuItem({ localiseKey: 'comments', iconId: 'comments', to: '/admin/comments' })}

      {renderChats && renderMenuItem({ localiseKey: 'chats', iconId: 'chats', to: '/admin/chats' })}

      <li className={classes['menu__section']}>{t('common.tools')}</li>

      {renderMenuItem({
        localiseKey: 'integration',
        iconId: 'integration',
        to: '/admin/integrations',
      })}

      {renderMenuItem({
        localiseKey: 'user-management',
        iconId: 'users',
        to: '/admin/userManagement',
      })}

      {renderMenuItem({ localiseKey: 'notification', iconId: 'bell', to: '/admin/notifications' })}

      {renderSettings &&
        renderMenuItem({
          localiseKey: 'settings',
          iconId: 'settings',
          to: '/admin/channelSettings',
        })}
    </ul>
  );
};
